import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getRetailStoreById, updateRetailStore, deleteRetailStore } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Building2,
  Phone,
  Save,
  AlertCircle,
  Star,
  Clock,
  MapPin,
  Globe,
  Tag,
  Trash2,
} from "lucide-react";

interface RetailFormData {
  storeName: string;
  description: string;
  storeType: string;
  category: string;
  businessHours: string;
  contactInfo: string;
  services: string;
  unitNumber: string;
  floor: string;
  building: string;
  website: string;
  isVerified: boolean;
  isFeatured: boolean;
}

const RETAIL_CATEGORIES = [
  { value: "CLOTHING", label: "Clothing" },
  { value: "ELECTRONICS", label: "Electronics" },
  { value: "HOME_GARDEN", label: "Home & Garden" },
  { value: "SPORTS", label: "Sports" },
  { value: "BOOKS", label: "Books" },
  { value: "BEAUTY", label: "Beauty" },
  { value: "TOYS", label: "Toys" },
  { value: "AUTOMOTIVE", label: "Automotive" },
  { value: "FOOD_BEVERAGE", label: "Food & Beverage" },
  { value: "HEALTHCARE", label: "Healthcare" },
  { value: "PROFESSIONAL_SERVICES", label: "Professional Services" },
  { value: "OTHER", label: "Other" },
];

const STORE_TYPES = [
  { value: "RETAIL_STORE", label: "Retail Store" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "SERVICE_SHOP", label: "Service Shop" },
  { value: "BOUTIQUE", label: "Boutique" },
  { value: "DEPARTMENT_STORE", label: "Department Store" },
  { value: "SPECIALTY_STORE", label: "Specialty Store" },
  { value: "OTHER", label: "Other" },
];

export default function EditRetailPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const updateRetailStoreFn = useAction(updateRetailStore);
  const deleteRetailStoreFn = useAction(deleteRetailStore);

  const { data: store, isLoading, error } = useQuery(getRetailStoreById, { storeId: itemId! });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RetailFormData>({
    storeName: "",
    description: "",
    storeType: "RETAIL_STORE",
    category: "OTHER",
    businessHours: "",
    contactInfo: "",
    services: "",
    unitNumber: "",
    floor: "",
    building: "",
    website: "",
    isVerified: false,
    isFeatured: false,
  });

  useEffect(() => {
    if (store) {
      setFormData({
        storeName: store.title || "",
        description: store.description || "",
        storeType: store.metadata?.storeType || "RETAIL_STORE",
        category: store.metadata?.category || "OTHER",
        businessHours: store.metadata?.businessHours || "",
        contactInfo: store.metadata?.contactInfo || "",
        services: store.metadata?.services?.join(", ") || "",
        unitNumber: store.metadata?.unitNumber || "",
        floor: store.metadata?.floor || "",
        building: store.metadata?.building || "",
        website: store.metadata?.website || "",
        isVerified: store.metadata?.isVerified || false,
        isFeatured: store.metadata?.isFeatured || false,
      });
    }
  }, [store]);

  const handleChange = (field: keyof RetailFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.storeName.trim()) newErrors.storeName = "Store name is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await updateRetailStoreFn({
        storeId: itemId!,
        storeName: formData.storeName.trim(),
        description: formData.description.trim(),
        storeType: formData.storeType as any,
        category: formData.category as any,
        businessHours: formData.businessHours.trim(),
        contactInfo: formData.contactInfo.trim(),
        services: formData.services.split(",").map((s) => s.trim()).filter(Boolean),
        unitNumber: formData.unitNumber.trim(),
        floor: formData.floor.trim(),
        building: formData.building.trim(),
        website: formData.website.trim(),
        isVerified: formData.isVerified,
        isFeatured: formData.isFeatured,
      });
      handleGoBack();
    } catch (error) {
      console.error("Error updating retail store:", error);
      setErrors({ submit: "Failed to update store." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this store?")) return;
    try {
      await deleteRetailStoreFn({ storeId: itemId! });
      handleGoBack();
    } catch (error) {
      console.error("Error deleting store:", error);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/retail`);
    } else {
      navigate("/retail");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to edit stores.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Store...">
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading store details...</p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (error || !store) {
    return (
      <IndividualEditPageLayout title="Store Not Found" icon={AlertCircle}>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Store Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The store you're looking for doesn't exist or you don't have
              permission to edit it.
            </p>
            <button
              onClick={() => navigate("/retail")}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors rounded-lg"
            >
              Back to Retail
            </button>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="retail-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Updating..." : "Update Store"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Danger Zone" hideOnMobile>
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete Store
        </button>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Retail Store"
      subtitle={`Administering ${formData.storeName}`}
      icon={Building2}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="retail-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Store Name</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.storeName}
                onChange={(e) => handleChange("storeName", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Categorization</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Category</label>
                <select value={formData.category} onChange={(e) => handleChange("category", e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all">
                  {RETAIL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Type</label>
                <select value={formData.storeType} onChange={(e) => handleChange("storeType", e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all">
                  {STORE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Business Hours</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={formData.businessHours} onChange={(e) => handleChange("businessHours", e.target.value)} className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all" />
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" /> Location</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Building</label>
                <input type="text" value={formData.building} onChange={(e) => handleChange("building", e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Unit</label>
                <input type="text" value={formData.unitNumber} onChange={(e) => handleChange("unitNumber", e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Website</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={formData.website} onChange={(e) => handleChange("website", e.target.value)} className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Phone className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <input type="text" value={formData.contactInfo} onChange={(e) => handleChange("contactInfo", e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all" />
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
